import { Box, Button } from "@mui/material";
import { useContext, useState } from "react";

import { ChatContext } from "../../contexts/chat/context";
import { ChatHistoryDropdown } from "./chat_history_dropdown";
import { ChatHistoryModal } from "./chat_history_modal";
import { ChatHistoryItem } from "../../contexts/chat/type";

export const ChatStatus = ({
  isConnected,
  isPreparing = false,
  onRetry,
}: {
  isConnected: boolean;
  isPreparing?: boolean;
  onRetry?: () => void;
}) => {
  const {
    chatContext,
    loadChatFromHistory,
    transportCapabilities,
    transportKind,
    isConnecting,
    platformConnectionError,
    platformConnectionErrorKind,
  } = useContext(ChatContext);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedHistoryItem, setSelectedHistoryItem] =
    useState<ChatHistoryItem | null>(null);

  // Only show as connected if both isConnected and chatContext are available
  const isActuallyConnected = isConnected && chatContext;
  const isConnectionPending = isConnecting || isPreparing;
  const platformStatusLabel = platformConnectionError
    ? platformConnectionErrorKind === "authentication_unavailable"
      ? "Authentication unavailable"
      : platformConnectionErrorKind === "connection_timeout"
        ? "Connection timed out"
        : "Unavailable"
    : "Unavailable";
  const platformConnectionDetail =
    transportKind === "platform" && !isActuallyConnected && !isConnectionPending
      ? platformConnectionError
      : null;

  const handleViewFullHistory = (item: ChatHistoryItem) => {
    setSelectedHistoryItem(item);
    setModalOpen(true);
  };

  const handleLoadToChat = (item: ChatHistoryItem) => {
    loadChatFromHistory(item);
    setModalOpen(false);
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          gap: 0.5,
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            minWidth: 0,
          }}
        >
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}
          >
            <Box
              sx={{
                backgroundColor: isActuallyConnected
                  ? "success.main"
                  : isConnectionPending
                    ? "warning.main"
                    : "action.active",
                width: 8,
                height: 8,
                borderRadius: "50%",
                flexShrink: 0,
                "@keyframes pulse": {
                  "0%": { opacity: 1 },
                  "50%": { opacity: 0.5 },
                  "100%": { opacity: 1 },
                },
                "@media (prefers-reduced-motion: no-preference)": {
                  animation: isActuallyConnected
                    ? "pulse 2s ease-in-out infinite"
                    : "none",
                },
              }}
            />
            <Box
              component="span"
              sx={{
                fontSize: "0.875rem",
                color: isActuallyConnected ? "text.primary" : "text.secondary",
              }}
            >
              {transportKind === "platform"
                ? isActuallyConnected
                  ? "Ready"
                  : isConnectionPending
                    ? "Checking availability..."
                    : platformStatusLabel
                : isActuallyConnected
                  ? "Connected"
                  : "Connecting..."}
            </Box>
          </Box>
          {transportKind === "platform" &&
            !isActuallyConnected &&
            !isConnectionPending &&
            onRetry && (
              <Button
                size="small"
                variant="text"
                onClick={onRetry}
                sx={{
                  minWidth: 0,
                  px: 0.75,
                  py: 0.25,
                  fontSize: "0.75rem",
                  lineHeight: 1.4,
                  textTransform: "none",
                  flexShrink: 0,
                }}
              >
                Retry connection
              </Button>
            )}
          {transportKind === "legacy" && transportCapabilities.history && (
            <ChatHistoryDropdown onViewFullHistory={handleViewFullHistory} />
          )}
        </Box>
        {platformConnectionDetail && (
          <Box
            role="status"
            aria-live="polite"
            sx={{
              pl: 2,
              pr: 0.5,
              minWidth: 0,
              color: "text.secondary",
              fontSize: "0.75rem",
              lineHeight: 1.35,
              overflowWrap: "anywhere",
            }}
          >
            {platformConnectionDetail}
          </Box>
        )}
      </Box>
      {transportKind === "legacy" && transportCapabilities.history && (
        <ChatHistoryModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          historyItem={selectedHistoryItem}
          onLoadToChat={handleLoadToChat}
        />
      )}
    </>
  );
};
